/**
 * GMB Posts Management Page
 * 
 * Features:
 * - List all GMB posts with filtering
 * - Create new posts with multi-location support
 * - View post performance metrics
 * - Manage drafts and scheduled posts
 */

import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Plus, 
  Send, 
  Calendar, 
  Image as ImageIcon, 
  Eye, 
  MousePointer, 
  Trash2, 
  Edit, 
  MapPin,
  AlertCircle,
  Filter,
  ChevronDown,
  ChevronUp,
  Building2,
  Clock
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { MultiLocationPostCreator } from '@/components/gmb/posts/MultiLocationPostCreator'
import { PostListItem } from '@/components/gmb/posts/PostListItem'
import { SyncStatusIndicator } from '@/components/gmb/shared/SyncStatusWidget'
import type { GmbPost, GmbLocation, GmbPostState, GmbPostTopicType } from '@/types/gmb'

/**
 * Main GMB Posts Management Page
 */
export function GMBPosts() {
  // State
  const [posts, setPosts] = useState<GmbPost[]>([])
  const [locations, setLocations] = useState<GmbLocation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreatePost, setShowCreatePost] = useState(false)
  
  // Filters
  const [selectedLocation, setSelectedLocation] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<GmbPostState | 'all'>('all')
  const [selectedType, setSelectedType] = useState<GmbPostTopicType | 'all'>('all')
  const [dateRange, setDateRange] = useState<'all' | '7days' | '30days' | '90days'>('30days')
  const [searchQuery, setSearchQuery] = useState('')

  // Load posts and locations
  useEffect(() => {
    loadData()
  }, [selectedLocation, selectedStatus, selectedType, dateRange])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load locations
      const { data: locationsData, error: locationsError } = await supabase
        .from('gmb_locations')
        .select('*')
        .eq('is_active', true)
        .order('location_name')

      if (locationsError) throw locationsError
      setLocations(locationsData || [])

      // Build posts query
      let postsQuery = supabase
        .from('gmb_posts')
        .select('*')
        .order('created_at', { ascending: false })

      // Apply filters
      if (selectedLocation !== 'all') {
        postsQuery = postsQuery.eq('gmb_location_id', selectedLocation)
      }

      if (selectedStatus !== 'all') {
        postsQuery = postsQuery.eq('state', selectedStatus)
      }

      if (selectedType !== 'all') {
        postsQuery = postsQuery.eq('topic_type', selectedType)
      }

      // Date range filter
      if (dateRange !== 'all') {
        const days = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 90
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - days)
        postsQuery = postsQuery.gte('created_at', startDate.toISOString())
      }

      const { data: postsData, error: postsError } = await postsQuery

      if (postsError) throw postsError

      // Filter by search query
      let filteredPosts = postsData || []
      if (searchQuery) {
        filteredPosts = filteredPosts.filter(post => 
          post.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.event_title?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      }

      setPosts(filteredPosts)
    } catch (err) {
      console.error('Error loading data:', err)
      setError('Failed to load posts')
    } finally {
      setLoading(false)
    }
  }

  // Group locations by franchise
  const groupedLocations = locations.reduce((acc, location) => {
    const franchise = location.franchise_region || 'Other'
    if (!acc[franchise]) {
      acc[franchise] = []
    }
    acc[franchise].push(location)
    return acc
  }, {} as Record<string, GmbLocation[]>)

  // Calculate post statistics
  const postStats = {
    total: posts.length,
    live: posts.filter(p => p.state === 'LIVE').length,
    scheduled: posts.filter(p => p.state === 'SCHEDULED').length,
    drafts: posts.filter(p => p.state === 'DRAFT').length,
    totalViews: posts.reduce((sum, p) => sum + (p.view_count || 0), 0),
    totalClicks: posts.reduce((sum, p) => sum + (p.click_count || 0), 0)
  }

  // Handle post deletion
  const handleDeletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return

    try {
      const { error } = await supabase
        .from('gmb_posts')
        .delete()
        .eq('id', postId)

      if (error) throw error
      
      // Refresh posts
      loadData()
    } catch (err) {
      console.error('Error deleting post:', err)
      alert('Failed to delete post')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">GMB Posts</h1>
          <p className="text-muted-foreground">
            Create and manage posts across all your Google My Business locations
          </p>
          <div className="mt-2">
            <SyncStatusIndicator syncType="posts" />
          </div>
        </div>
        <Button onClick={() => setShowCreatePost(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Post
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{postStats.total}</div>
            <p className="text-xs text-muted-foreground">
              All time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Live Posts</CardTitle>
            <Badge variant="default" className="h-5">
              <span className="text-xs">LIVE</span>
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{postStats.live}</div>
            <p className="text-xs text-muted-foreground">
              Currently active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scheduled</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{postStats.scheduled}</div>
            <p className="text-xs text-muted-foreground">
              Pending publish
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{postStats.totalViews.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              All posts combined
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{postStats.totalClicks.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              CTA engagement
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Location Filter */}
            <div>
              <Label htmlFor="location-filter" className="text-xs">Location</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger id="location-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {Object.entries(groupedLocations).map(([franchise, locs]) => (
                    <div key={franchise}>
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                        {franchise}
                      </div>
                      {locs.map(location => (
                        <SelectItem key={location.id} value={location.gmb_location_id}>
                          {location.location_name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div>
              <Label htmlFor="status-filter" className="text-xs">Status</Label>
              <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as GmbPostState | 'all')}>
                <SelectTrigger id="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="LIVE">Live</SelectItem>
                  <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                  <SelectItem value="REMOVED">Removed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Type Filter */}
            <div>
              <Label htmlFor="type-filter" className="text-xs">Post Type</Label>
              <Select value={selectedType} onValueChange={(v) => setSelectedType(v as GmbPostTopicType | 'all')}>
                <SelectTrigger id="type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="EVENT">Event</SelectItem>
                  <SelectItem value="OFFER">Offer</SelectItem>
                  <SelectItem value="PRODUCT">Product</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Filter */}
            <div>
              <Label htmlFor="date-filter" className="text-xs">Date Range</Label>
              <Select value={dateRange} onValueChange={(v) => setDateRange(v as any)}>
                <SelectTrigger id="date-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="7days">Last 7 Days</SelectItem>
                  <SelectItem value="30days">Last 30 Days</SelectItem>
                  <SelectItem value="90days">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div>
              <Label htmlFor="search" className="text-xs">Search</Label>
              <Input
                id="search"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Posts List or Create Form */}
      {showCreatePost ? (
        <MultiLocationPostCreator
          locations={locations}
          groupedLocations={groupedLocations}
          onClose={() => {
            setShowCreatePost(false)
            loadData()
          }}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Posts</CardTitle>
            <CardDescription>
              {posts.length} post{posts.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading posts...
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : posts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No posts found</p>
                <Button onClick={() => setShowCreatePost(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Post
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map(post => (
                  <PostListItem
                    key={post.id}
                    post={post}
                    locations={locations}
                    onDelete={handleDeletePost}
                    onEdit={() => {
                      // TODO: Implement edit functionality
                      alert('Edit functionality coming soon')
                    }}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default GMBPosts